import React, { cloneElement, useEffect, useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

import {
    oneOfConditional,
    reusableComponentSizes,
    buttonStyleTypes,
    circleButtonStyleTypes,
    justifyContentPosition,
    positionalAlignments,
    popoverPlacements
} from "core/props";

import { usePopoverState, PopoverDisclosure } from "reakit/Popover";
import {
    PopoverTriggerButton,
    ReakitPopover,
    Container,
    ReakitPopoverArrow,
    WrongPropTypeError
} from "./styles";

const PopoverContainer = ({
    popoverState,
    disclosure,
    darkMode,
    children,
    isFluidWidth,
    ...props
}) => {
    return (
        <>
            <PopoverDisclosure
                {...popoverState}
                ref={disclosure.ref}
                {...disclosure.props}
            >
                {disclosureProps => cloneElement(disclosure, disclosureProps)}
            </PopoverDisclosure>

            <ReakitPopover
                tabIndex={0}
                hideOnEsc
                hideOnClickOutside
                {...popoverState}
                {...props}
            >
                <Container darkMode={darkMode} isFluidWidth={isFluidWidth}>
                    <ReakitPopoverArrow {...popoverState} />
                    {children}
                </Container>
            </ReakitPopover>
        </>
    );
};

const UnstyledPopover = ({
    className,
    children,
    triggerTextOrElement,
    ariaLabel,
    popoverPlacement = "auto",
    isFluidWidth = false,
    darkMode = false,
    buttonDarkMode = false,
    isCircleTrigger = false,
    buttonSize = "md",
    buttonStyleType,
    buttonIsDisabled = false,
    buttonIsProcessing = false,
    buttonIsActive = false,
    buttonJustifyContent,
    buttonIconPosition,
    buttonWidth,
    buttonIsPill = false,
    circleToggledBgColor,
    circleToggledIconColor,
    onPopoverOpen,
    onPopoverClose,
    ...additionalButtonProps
}) => {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const popoverState = usePopoverState({
        animated: 1,
        unstable_flip: true,
        placement: popoverPlacement,
        gutter: 12 + 4 // 12 is the Reakit default to make it appear on the edge, and then 4px is the additional space between trigger and arrow
    });

    useEffect(() => {
        const popoverIsOpening = !isPopoverOpen && popoverState.visible;

        if (popoverIsOpening) {
            typeof onPopoverOpen === "function" && onPopoverOpen();
            setIsPopoverOpen(true);
        }
    }, [popoverState.visible, isPopoverOpen]);

    useEffect(() => {
        const popoverIsClosing = isPopoverOpen && !popoverState.visible;

        if (popoverIsClosing) {
            typeof onPopoverClose === "function" && onPopoverClose();
            setIsPopoverOpen(false);
        }
    }, [popoverState.visible, isPopoverOpen]);

    const isTriggerButtonOrLink =
        triggerTextOrElement.type === "button" ||
        triggerTextOrElement.type === "a";

    const ariaLabelString = `${
        popoverState.visible ? "Close" : "Open"
    } ${ariaLabel}`;

    const buttonSpecificProps = isCircleTrigger
        ? {
              toggledBgColor: circleToggledBgColor,
              toggledIconColor: circleToggledIconColor
          }
        : {
              justifyContent: buttonJustifyContent,
              iconPosition: buttonIconPosition,
              width: buttonWidth,
              isPill: buttonIsPill
          };

    return isTriggerButtonOrLink ? (
        <WrongPropTypeError>
            triggerTextOrElement is{" "}
            {triggerTextOrElement.type === "button"
                ? "a button"
                : "an anchor tag"}
            . Only pass React elements or strings to the prop.
        </WrongPropTypeError>
    ) : (
        <PopoverContainer
            className={className}
            aria-label={ariaLabel}
            popoverState={popoverState}
            darkMode={darkMode}
            isFluidWidth={isFluidWidth}
            disclosure={
                <PopoverTriggerButton
                    aria-label={
                        typeof triggerTextOrElement !== "string"
                            ? ariaLabelString
                            : null
                    }
                    isCircleTrigger={isCircleTrigger}
                    darkMode={buttonDarkMode}
                    size={buttonSize}
                    styleType={buttonStyleType}
                    isDisabled={buttonIsDisabled}
                    isProcessing={buttonIsProcessing}
                    isActive={buttonIsActive}
                    isToggled={popoverState.visible}
                    {...buttonSpecificProps}
                    {...additionalButtonProps}
                >
                    {triggerTextOrElement}
                </PopoverTriggerButton>
            }
        >
            {/* For better performance - do not render children if not shown */}
            {isPopoverOpen && children}
        </PopoverContainer>
    );
};

const Popover = styled(UnstyledPopover)``;

Popover.propTypes = {
    triggerTextOrElement: PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.string
    ]).isRequired,
    ariaLabel: PropTypes.string.isRequired,
    popoverPlacement: PropTypes.oneOf(popoverPlacements),
    isFluidWidth: PropTypes.bool,
    darkMode: PropTypes.bool,
    isCircleTrigger: PropTypes.bool,
    buttonSize: PropTypes.oneOf(reusableComponentSizes),
    buttonStyleType: oneOfConditional({
        conditionArray1: circleButtonStyleTypes,
        conditionArray2: buttonStyleTypes,
        condition: props => props.isCircleTrigger === true
    }),
    buttonIsDisabled: PropTypes.bool,
    buttonIsActive: PropTypes.bool,
    buttonIsProcessing: PropTypes.bool,
    buttonIsPill: PropTypes.bool,
    buttonJustifyContent: PropTypes.oneOf(justifyContentPosition),
    buttonIconPosition: PropTypes.oneOf(positionalAlignments),
    buttonWidth: PropTypes.string,
    circleToggledBgColor: PropTypes.string,
    circleToggledIconColor: PropTypes.string,
    onPopoverClose: PropTypes.func,
    onPopoverOpen: PropTypes.func
};

export default Popover;
